import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useScreen } from '../../app/shell-context';
import { formatDayYear } from '../../lib/time';
import { hoursSummary } from '../../lib/org';
import { pluralWithCount } from '../../lib/plural';
import { reminderCopy } from '../../lib/reminders';
import { ChevronIcon } from '../../ui/icons';
import { Pill, Row } from '../../ui/parts';
import { FormSkeleton, LoadFailed } from '../org-parts';
import { useAdminDirections, useAdminList, useAdminSettings, useSubscription } from '../api';
import { zoneLabel } from './OrgSchedule';
import { planLabel, subscriptionPill } from './OrgSubscription';

/**
 * Вкладка «Организация» — оглавление, а не форма.
 *
 * Раньше здесь стояло всё сразу: профиль для клиентов, адреса, часовой пояс,
 * правила записи, девять тумблеров уведомлений и подписка — одним свитком с
 * одной кнопкой сохранения на всё. Найти в нём нужное можно было только
 * прокруткой, а нажатие «Сохранить» отправляло на сервер и то, чего не трогали.
 *
 * Теперь каждый раздел — свой экран со своей кнопкой. Здесь остаётся список
 * переходов, и у каждого — короткая сводка: она отвечает на вопрос «надо ли
 * туда заходить» без захода. Сводки собираются из уже загруженных данных —
 * второго запроса ради подписи под строкой не делается.
 */
export function Org() {
  const navigate = useNavigate();
  const data = useAdminSettings();
  const directions = useAdminDirections();
  const admins = useAdminList();
  const sub = useSubscription();

  useScreen('Организация', null);

  if (data.isError) {
    return (
      <Screen>
        <LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} />
      </Screen>
    );
  }

  if (!data.data) {
    return (
      <Screen>
        <FormSkeleton rows={6} />
      </Screen>
    );
  }

  const { settings, locations, hours, notifications } = data.data;
  const tf = settings.time_format;
  const primary = locations.find((l) => l.is_primary) ?? locations[0] ?? null;
  const dirs = (directions.data ?? []).filter((d) => d.is_active);
  const subscription = sub.data?.subscription ?? null;
  const plans = sub.data?.plans ?? {};
  const subUntil = subscription
    ? (subscription.status === 'trial' ? subscription.trial_ends_at : subscription.current_period_end)
    : null;

  return (
    <Screen>
      {/* Заголовок секции здесь повторял бы слово в слово и шапку экрана, и вкладку
          внизу. Остаётся объяснение — оно говорит то, чего ещё не сказали. */}
      <p className="lead">Как организация выглядит для клиентов и по каким правилам работает запись</p>

      <div className="rows">
        <Row
          title="Профиль организации"
          note={[settings.title, settings.phone].filter(Boolean).join(' · ')
            || 'Логотип, название, описание и контакты'}
          onClick={() => navigate('/admin/org/profile')}
        />

        <Row
          title="Адреса"
          note={locations.length
            ? `${pluralWithCount(locations.length, 'адрес', 'адреса', 'адресов')}${primary ? ` · ${primary.title}` : ''}`
            : 'Ни одного адреса — клиент не увидит, куда идти'}
          onClick={() => navigate('/admin/org/addresses')}
        />

        <Row
          title="Расписание и запись"
          note={`${hoursSummary(hours, tf)} · ${zoneLabel(settings.tz)}`}
          onClick={() => navigate('/admin/org/schedule')}
        />

        <Row
          title="Уведомления"
          note={reminderCopy(notifications.reminders, 'Напоминания') || 'Напоминания выключены'}
          onClick={() => navigate('/admin/org/notifications')}
        />

        {subscription ? (
          <Row
            title="Подписка"
            note={subUntil ? `до ${formatDayYear(subUntil, settings.tz)}` : undefined}
            aside={(
              <>
                <Pill kind={subscriptionPill(subscription.status)}>
                  {planLabel(subscription.status, subscription.plan, plans)}
                </Pill>
                <span className="chev"><ChevronIcon /></span>
              </>
            )}
            onClick={() => navigate('/admin/org/subscription')}
          />
        ) : null}

        <Row
          title="Категории услуг"
          note={dirs.length
            ? pluralWithCount(dirs.length, 'категория', 'категории', 'категорий')
            : 'Категорий пока нет'}
          onClick={() => navigate('/admin/org/categories')}
        />

        <Row
          title="Администраторы"
          note={admins.data
            ? pluralWithCount(admins.data.length, 'человек', 'человека', 'человек')
            : 'Кто управляет организацией'}
          onClick={() => navigate('/admin/org/admins')}
        />

        <Row
          title="Журнал действий"
          note="Кто и что менял"
          onClick={() => navigate('/admin/org/audit')}
        />
      </div>
    </Screen>
  );
}
